import { PropertyTypes } from "epsm-api-types";
import type { BooleanOptions, ColumnType } from "epsm-api-types/models";
import { CURRENCY, LOCALE, UTC_TIME_ZONE } from "@/config";

export const DEFAULT_NUMBER_OPTIONS: Intl.NumberFormatOptions = {
	style: "decimal",
	minimumFractionDigits: 0,
	maximumFractionDigits: 2,
};

export const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
	timeZone: UTC_TIME_ZONE,
	month: "2-digit",
	day: "2-digit",
	year: "2-digit",
};

export const DEFAULT_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
	timeZone: undefined,
	hour: "2-digit",
	minute: "2-digit",
	month: undefined,
	day: undefined,
	year: undefined,
};

const DEFAULT_EMPTY_VALUE = "";

export const DEFAULT_PERCENT_OPTIONS: Intl.NumberFormatOptions = {
	style: "percent",
	minimumFractionDigits: 0,
	maximumFractionDigits: 2,
};

export const DEFAULT_BIGNUMBER_OPTIONS: Intl.NumberFormatOptions = {
	style: "decimal",
	minimumFractionDigits: 0,
	maximumFractionDigits: 3,
	notation: "compact",
};
export const DEFAULT_CURRENCY_OPTIONS: Intl.NumberFormatOptions = {
	notation: "compact",
	style: "currency",
	currency: CURRENCY,
	maximumFractionDigits: 2,
};
export const DEFAULT_FULL_CURRENCY_OPTIONS: Intl.NumberFormatOptions = {
	style: "currency",
	currency: CURRENCY,
	maximumFractionDigits: 2,
};

type FormatOptions =
	| Intl.NumberFormatOptions
	| Intl.DateTimeFormatOptions
	| BooleanOptions;

export const parseNumber = (
	value: string,
	locale = LOCALE,
	currency = CURRENCY,
) => {
	if (
		value === undefined ||
		value === null ||
		value === "" ||
		value === "-" ||
		typeof value === "number"
	) {
		return value;
	}
	const percentSeparator = Intl.NumberFormat(locale, {
		style: "percent",
		maximumFractionDigits: 0,
	})
		.format(0)
		.replace(/\p{Number}/gu, "");
	const currencySeparator = Intl.NumberFormat(locale, {
		style: "currency",
		currency: currency,
		maximumFractionDigits: 0,
	})
		.format(0)
		.replace(/\p{Number}/gu, "");
	const thousandSeparator = Intl.NumberFormat(locale)
		.format(11111)
		.replace(/\p{Number}/gu, "");
	const decimalSeparator = Intl.NumberFormat(locale)
		.format(1.1)
		.replace(/\p{Number}/gu, "");
	const valC = currencySeparator
		? value.replace(new RegExp(`\\${currencySeparator}`, "g"), "")
		: value;
	const val = percentSeparator
		? valC.replace(new RegExp(`\\${percentSeparator}`, "g"), "")
		: valC;

	const parseScale = {
		K: 3,
		M: 6,
		B: 9,
		T: 12,
		P: 15,
		E: 18,
	};
	const scale = parseScale[value.slice(-1) as keyof typeof parseScale] || 0;
	const baseNumber = Number.parseFloat(
		val
			.replace(new RegExp(`\\${thousandSeparator}`, "g"), "")
			.replace(new RegExp(`\\${decimalSeparator}`), "."),
	);

	return baseNumber * 10 ** scale;
};

export const formatValueCurrency = (
	value: unknown,
	defaultEmptyValue = DEFAULT_EMPTY_VALUE,
) =>
	formatValue(
		value,
		PropertyTypes.NUMBER,
		DEFAULT_CURRENCY_OPTIONS,
		defaultEmptyValue,
	);

export const formatValueDate = (
	value: unknown,
	defaultEmptyValue = DEFAULT_EMPTY_VALUE,
) =>
	formatValue(
		value,
		PropertyTypes.DATE,
		DEFAULT_DATE_OPTIONS,
		defaultEmptyValue,
	);

export const formatValueTime = (
	value: unknown,
	defaultEmptyValue = DEFAULT_EMPTY_VALUE,
) =>
	formatValue(
		value,
		PropertyTypes.DATE,
		DEFAULT_TIME_OPTIONS,
		defaultEmptyValue,
	);

export const formatValuePercent = (
	value: unknown,
	defaultEmptyValue = DEFAULT_EMPTY_VALUE,
) =>
	formatValue(
		value,
		PropertyTypes.NUMBER,
		DEFAULT_PERCENT_OPTIONS,
		defaultEmptyValue,
	);

export const formatValueNumber = (
	value: unknown,
	defaultEmptyValue = DEFAULT_EMPTY_VALUE,
) =>
	formatValue(
		value,
		PropertyTypes.NUMBER,
		DEFAULT_NUMBER_OPTIONS,
		defaultEmptyValue,
	);

export const formatValueBigNumber = (
	value: unknown,
	defaultEmptyValue = DEFAULT_EMPTY_VALUE,
) =>
	formatValue(
		value,
		PropertyTypes.NUMBER,
		DEFAULT_BIGNUMBER_OPTIONS,
		defaultEmptyValue,
	);

export const formatValue = (
	value: unknown,
	type: ColumnType,
	options: FormatOptions = {},
	defaultEmptyValue = DEFAULT_EMPTY_VALUE,
): string => {
	if (value === undefined || value === null) {
		return defaultEmptyValue;
	}

	try {
		switch (type) {
			case PropertyTypes.NUMBER: {
				return Intl.NumberFormat(LOCALE, {
					...DEFAULT_NUMBER_OPTIONS,
					...(options as Intl.NumberFormatOptions),
				}).format(value as number);
			}

			case PropertyTypes.DATE:
				return Intl.DateTimeFormat(LOCALE, {
					...DEFAULT_DATE_OPTIONS,
					...(options as Intl.DateTimeFormatOptions),
				}).format(value as Date);

			case PropertyTypes.BOOLEAN: {
				const opt = options as BooleanOptions;
				return value === true ? (opt?.true ?? "True") : (opt?.false ?? "False");
			}

			default:
				return value.toString();
		}
	} catch (err) {
		console.warn({ err, value });
		return defaultEmptyValue;
	}
};
